import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";

export default function Insights() {
  useSEO({
    title: "Insights — Field Notes on ETRM, CTRM & Trading Technology | EnPrex Services",
    description: "Practitioner insights on ETRM, CTRM, Openlink Endur, digital transformation, AI in trading, cloud modernization and the future of commodity trading.",
    path: "/insights",
  });
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div data-testid="insights-page">
      <section className="container-x pt-24 pb-12 border-b border-white/5">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-6 font-mono">— Insights</div>
          <h1 className="font-display text-5xl md:text-7xl font-light text-white tracking-tighter leading-[0.95] max-w-4xl">
            Field notes from the <span className="italic">trading floor</span>.
          </h1>
        </motion.div>
      </section>

      {loading && <div className="container-x py-20 text-slate-400 font-mono text-sm">Loading insights…</div>}

      {featured && (
        <section className="container-x py-16 border-b border-white/5">
          <Link to={`/insights/${featured.slug}`} data-testid={`featured-post-${featured.slug}`} className="grid md:grid-cols-2 gap-12 items-center group">
            <div className="aspect-[16/10] overflow-hidden order-2 md:order-1">
              <img src={featured.cover_image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="order-1 md:order-2">
              <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-6">
                <span className="text-[#D4AF37]">Featured</span>
                <span className="w-8 h-px bg-slate-600" />
                <span>{featured.category}</span>
              </div>
              <h2 className="font-display text-3xl md:text-5xl text-white font-light tracking-tight leading-tight group-hover:text-[#D4AF37] transition-colors">
                {featured.title}
              </h2>
              <p className="mt-6 text-lg text-slate-400 leading-relaxed font-light">{featured.excerpt}</p>
              <div className="mt-8 flex items-center gap-3 text-sm text-slate-300">
                <span className="font-medium">{featured.author}</span>
                <span className="w-4 h-px bg-slate-600" />
                <span className="font-mono text-xs">{featured.read_time}</span>
              </div>
            </div>
          </Link>
        </section>
      )}

      <section className="container-x py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {rest.map((p) => (
            <Link
              key={p.slug}
              to={`/insights/${p.slug}`}
              data-testid={`insight-card-${p.slug}`}
              className="group border border-white/5 hover:border-[#D4AF37]/40 bg-white/[0.02] transition-all"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-8">
                <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-4">
                  <span className="text-[#D4AF37]">{p.category}</span>
                  <span className="w-4 h-px bg-slate-600" />
                  <span>{p.read_time}</span>
                </div>
                <h3 className="font-display text-xl text-white font-medium leading-tight group-hover:text-[#D4AF37] transition-colors">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm text-slate-400 line-clamp-2">{p.excerpt}</p>
                <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 group-hover:text-[#D4AF37] transition-colors font-mono">
                  Read <ArrowUpRight size={12} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
